import React, { useEffect, useMemo, useState } from 'react';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CloseIcon from '@mui/icons-material/Close';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import NorthEastOutlinedIcon from '@mui/icons-material/NorthEastOutlined';
import SouthEastOutlinedIcon from '@mui/icons-material/SouthEastOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../../contexts/AuthContext';
import { recoveryWalletApi } from '../../utils/recoveryWalletApi';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Pagination,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

const formatTokens = (amount) => `${new Intl.NumberFormat('en-US').format(amount || 0)} TND`;

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatTimeRemaining = (daysRemaining) => {
  if (daysRemaining <= 0) return 'Expired';
  if (daysRemaining === 1) return '1 day';
  if (daysRemaining < 30) return `${daysRemaining} days`;
  const months = Math.floor(daysRemaining / 30);
  const days = daysRemaining % 30;
  if (days === 0) return `${months} ${months === 1 ? 'month' : 'months'}`;
  return `${months}m ${days}d`;
};

const getExpirationColor = (daysRemaining) => {
  if (daysRemaining <= 0) return 'error';
  if (daysRemaining <= 7) return 'error';
  if (daysRemaining <= 30) return 'warning';
  return 'success';
};

const RecoveryWallet = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = isDownSm;
  const borderRadius = 1;
  const compactRadius = 0.6;
  
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const userId = user?._id || user?.id;

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const fetchWalletData = async () => {
    if (!userId) return;
    try {
      setError(null);
      const response = await recoveryWalletApi.getWallet(userId);
      if (response?.success) {
        setWalletData(response.data);
      } else {
        setError('Failed to load wallet data');
      }
    } catch (err) {
      console.error('Error fetching recovery wallet:', err);
      setError('Unable to connect to wallet service');
    }
  };

  const fetchTransactions = async (page = 1) => {
    if (!userId) return;
    try {
      setError(null);
      const response = await recoveryWalletApi.getTransactions(userId, {
        page,
        limit: 10,
        filter: transactionFilter,
      });

      if (response?.success) {
        setTransactions(response.data);
        setTotalPages(response.pagination?.pages || 1);
        setCurrentPage(response.pagination?.page || 1);
      } else {
        setError('Failed to load transaction history');
      }
    } catch (err) {
      console.error('Error fetching recovery transactions:', err);
      setError('Unable to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!userId) return;
    try {
      const response = await recoveryWalletApi.getStats(userId);
      if (response?.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching recovery stats:', err);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (!userId) {
      setLoading(false);
      return undefined;
    }

    (async () => {
      setLoading(true);
      await Promise.all([fetchWalletData(), fetchTransactions(1), fetchStats()]);
      if (mounted) {
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, transactionFilter]);

  const handleWithdraw = async () => {
    if (!walletData?.balance || walletData.balance <= 0 || !userId) {
      showSnackbar('No recovery TND available for withdrawal', 'error');
      return;
    }

    setWithdrawing(true);
    try {
      const response = await recoveryWalletApi.withdraw(userId);

      if (response?.success) {
        await Promise.all([fetchWalletData(), fetchTransactions(1), fetchStats()]);
        setShowWithdrawModal(false);
        showSnackbar(
          `Withdrawal successful! You received ${formatTokens(response.data.receivedAmount)} (50% of ${formatTokens(response.data.totalRecoveryAmount)} recovery balance).`,
          'success'
        );
      } else {
        showSnackbar('Withdrawal failed. Please try again.', 'error');
      }
    } catch (err) {
  console.error('Error withdrawing recovery TND:', err);
      showSnackbar('Unable to process withdrawal. Please try again.', 'error');
    } finally {
      setWithdrawing(false);
    }
  };

  const summaryCards = useMemo(() => {
    const balance = walletData?.balance || 0;
    return [
      {
        label: 'Current Balance',
        value: formatTokens(balance),
        emoji: '🪙',
        helper: 'Available for withdrawal',
        icon: <SavingsOutlinedIcon fontSize="large" />,
        accent: 'primary',
        gradient: 'linear-gradient(135deg, rgba(0,172,193,0.12), rgba(63,180,255,0.20))',
        action: {
          label: balance > 0 ? 'Withdraw 50%' : 'No Balance',
          disabled: balance <= 0,
          onClick: () => setShowWithdrawModal(true),
        },
      },
      {
        label: 'Total Earned',
        value: formatTokens(walletData?.totalEarned),
        emoji: '📈',
        helper: 'From lost auction fees',
        icon: <TrendingUpIcon fontSize="large" />,
        accent: 'success',
        gradient: 'linear-gradient(135deg, rgba(76,175,80,0.12), rgba(129,199,132,0.20))',
      },
      {
        label: 'Total Spent',
        value: formatTokens(walletData?.totalSpent),
        emoji: '🛒',
        helper: 'On ecommerce purchases',
        icon: <ShoppingCartCheckoutIcon fontSize="large" />,
        accent: 'warning',
        gradient: 'linear-gradient(135deg, rgba(255,152,0,0.12), rgba(255,204,128,0.20))',
      },
      {
        label: 'Total Withdrawn',
        value: formatTokens(walletData?.totalWithdrawn),
        emoji: '💰',
        helper: 'Transferred to main wallet',
        icon: <AccountBalanceWalletIcon fontSize="large" />,
        accent: 'secondary',
        gradient: 'linear-gradient(135deg, rgba(156,39,176,0.12), rgba(222,173,233,0.20))',
      },
    ];
  }, [walletData]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3,
        bgcolor: theme.palette.mode === 'dark' ? '#0a0e27' : '#ffffff',
        minHeight: '100vh',
        p: 3,
        color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000'
      }}
    >
      {/* Hero / Header Section */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  variant="rounded"
                  sx={{ 
                    width: { xs: 56, md: 64 }, 
                    height: { xs: 56, md: 64 }, 
                    bgcolor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: compactRadius 
                  }}
                >
                  <SavingsOutlinedIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography 
                    variant={isDownSm ? "h4" : "h3"} 
                    fontWeight={800} 
                    sx={{ 
                      letterSpacing: '-0.02em',
                      color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000'
                    }}
                  >
                    Recovery Wallet
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mt: 0.5,
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'
                    }}
                  >
                    Earn, spend, and withdraw your recovery tokens
                  </Typography>
                </Box>
              </Stack>
              
              <Tooltip title="Refresh wallet data" placement="left">
                <Button
                  variant="outlined"
                  startIcon={<ReplayOutlinedIcon />}
                  onClick={() => {
                    setLoading(true);
                    Promise.all([fetchWalletData(), fetchTransactions(currentPage), fetchStats()]).finally(() =>
                      setLoading(false)
                    );
                  }}
                  sx={{ 
                    borderRadius: compactRadius,
                    height: 44,
                    minWidth: { xs: '100%', md: 140 }
                  }}
                >
                  Refresh
                </Button>
              </Tooltip>
            </Stack>

      {/* Main Content */}
      {loading ? (
            <Grid container spacing={3}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={`skeleton-${index}`}>
                  <Card sx={{ borderRadius, p: 2.5, height: '100%' }}>
                    <Skeleton variant="text" width="60%" height={30} />
                    <Skeleton variant="text" width="80%" height={50} sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="50%" height={20} sx={{ mt: 0.5 }} />
                    <Skeleton variant="rectangular" height={40} sx={{ mt: 2, borderRadius: compactRadius }} />
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : error ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 10,
                px: 2,
                borderRadius,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
              }}
            >
              <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography 
                variant="h5" 
                fontWeight={700} 
                gutterBottom 
                sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
              >
                Error Loading Wallet
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  maxWidth: 400, 
                  mx: 'auto', 
                  mb: 3,
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' 
                }}
              >
                {error}
              </Typography>
              <Button
                variant="contained"
                startIcon={<ReplayOutlinedIcon />}
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  Promise.all([fetchWalletData(), fetchTransactions(currentPage), fetchStats()]).finally(() =>
                    setLoading(false)
                  );
                }}
                sx={{ borderRadius: compactRadius }}
              >
                Try Again
              </Button>
            </Box>
          ) : (
            <Stack spacing={4}>
              {/* Summary Cards */}
              <Fade in timeout={600}>
                <Grid container spacing={3}>
                  {summaryCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={card.label}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%',
                          position: 'relative',
                          overflow: 'hidden',
                          borderRadius,
                          background: card.gradient,
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                          border: '1px solid',
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[4],
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Stack spacing={2}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: compactRadius, 
                                bgcolor: `${card.accent}.main`,
                                color: `${card.accent}.contrastText`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {card.icon}
                              </Box>
                              <Typography variant="h3" sx={{ opacity: 0.3, lineHeight: 1 }}>
                                {card.emoji}
                              </Typography>
                            </Stack>
                            <Box>
                              <Typography 
                                variant="caption" 
                                textTransform="uppercase" 
                                fontWeight={600} 
                                sx={{ 
                                  letterSpacing: '0.5px',
                                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' 
                                }}
                              >
                                {card.label}
                              </Typography>
                              <Typography 
                                variant="h4" 
                                fontWeight={800} 
                                sx={{ 
                                  my: 0.5,
                                  color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' 
                                }}
                              >
                                {card.value}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                              >
                                {card.helper}
                              </Typography>
                            </Box>
                            {card.action && (
                              <Button
                                fullWidth
                                variant="contained"
                                color={card.accent}
                                onClick={card.action.onClick}
                                disabled={card.action.disabled}
                                disableElevation
                                sx={{ 
                                  borderRadius: compactRadius,
                                  fontWeight: 600,
                                  textTransform: 'none'
                                }}
                              >
                                {card.action.label}
                              </Button>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Fade>

              {/* How It Works & Statistics Section */}
              <Grid container spacing={3}>
                {/* How Recovery Wallet Works */}
                <Grid item xs={12} md={stats ? 6 : 12}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%',
                      borderRadius,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          borderRadius: compactRadius, 
                          bgcolor: 'info.main',
                          color: 'info.contrastText',
                          display: 'flex'
                        }}>
                          <InfoOutlinedIcon />
                        </Box>
                        <Typography 
                          variant="h5" 
                          fontWeight={700}
                          sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                        >
                          How It Works
                        </Typography>
                      </Stack>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <EmojiEventsOutlinedIcon color="warning" />
                          <Typography 
                            variant="body2" 
                            sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}
                          >
                    Earn TND from bidding fees when you do not win auctions.
                  </Typography>
                </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <ShoppingCartCheckoutIcon color="success" />
                          <Typography 
                            variant="body2" 
                            sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}
                          >
                            Spend recovery TND only in the ecommerce section.
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <WarningAmberOutlinedIcon color="error" />
                          <Typography 
                            variant="body2" 
                            sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}
                          >
                            TND can't be withdrawn, transferred, or used for bidding.
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <AccountBalanceWalletIcon color="secondary" />
                          <Typography 
                            variant="body2" 
                            sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}
                          >
                    Combine with main wallet and online payments—no expiration.
                  </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Statistics */}
                {stats && (
                  <Grid item xs={12} md={6}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        height: '100%',
                        borderRadius,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: compactRadius, 
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'flex'
                          }}>
                            <TrendingUpIcon />
                          </Box>
                          <Typography 
                            variant="h5" 
                            fontWeight={700}
                            sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                          >
                            Statistics
                          </Typography>
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Card variant="outlined" sx={{ borderRadius: compactRadius, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.01)' : '#fafafa' }}>
                              <CardContent>
                                <Typography 
                                  variant="overline" 
                                  sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                                >
                                  Times earned
                                </Typography>
                                <Typography 
                                  variant="h5" 
                                  fontWeight={700}
                                  sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                                >
                                  {stats.transactionCount?.earned || 0}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6}>
                            <Card variant="outlined" sx={{ borderRadius: compactRadius, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.01)' : '#fafafa' }}>
                              <CardContent>
                                <Typography 
                                  variant="overline" 
                                  sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                                >
                                  Times spent
                                </Typography>
                                <Typography 
                                  variant="h5" 
                                  fontWeight={700}
                                  sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                                >
                                  {stats.transactionCount?.spent || 0}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6}>
                            <Card variant="outlined" sx={{ borderRadius: compactRadius, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.01)' : '#fafafa' }}>
                              <CardContent>
                                <Typography 
                                  variant="overline" 
                                  sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                                >
                                  Avg. earned
                                </Typography>
                                <Typography 
                                  variant="h5" 
                                  fontWeight={700}
                                  sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                                >
                                  {formatTokens(stats.averageEarned)}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6}>
                            <Card variant="outlined" sx={{ borderRadius: compactRadius, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.01)' : '#fafafa' }}>
                              <CardContent>
                                <Typography 
                                  variant="overline" 
                                  sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                                >
                                  Avg. spent
                                </Typography>
                                <Typography 
                                  variant="h5" 
                                  fontWeight={700}
                                  sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                                >
                                  {formatTokens(stats.averageSpent)}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

            {/* Transaction History Section */}
            <Card 
              elevation={0}
              sx={{ 
                borderRadius,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={2}
                  sx={{ mb: 3 }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: compactRadius, 
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex'
                      }}>
                        <HistoryOutlinedIcon />
                      </Box>
                      <Box>
                        <Typography 
                          variant="h5" 
                          fontWeight={700}
                          sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                        >
                          Transaction History
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}
                        >
                          Track all earnings, spending, and withdrawals
                        </Typography>
                      </Box>
                    </Stack>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 0.5,
                        borderRadius: compactRadius,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff',
                        minWidth: { xs: '100%', sm: 220 },
                      }}
                    >
                      <FormControl size="small" fullWidth variant="standard">
                        <Select
                          value={transactionFilter}
                          onChange={(event) => {
                            setTransactionFilter(event.target.value);
                            fetchTransactions(1);
                          }}
                          disableUnderline
                          startAdornment={
                            <FilterListIcon sx={{ ml: 1.5, mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          }
                          sx={{
                            '& .MuiSelect-select': {
                              py: 1,
                              px: 1,
                              fontWeight: 600,
                            },
                          }}
                        >
                          <MenuItem value="all">All Transactions</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="expired">Expired</MenuItem>
                          <MenuItem value="withdrawn">Withdrawn</MenuItem>
                        </Select>
                      </FormControl>
                    </Paper>
                  </Stack>

                  {transactions.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 8,
                        px: 2,
                        borderRadius: compactRadius,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                      }}
                    >
                      <Typography variant="h1" sx={{ fontSize: 64, mb: 2 }}>💸</Typography>
                      <Typography 
                        variant="h5" 
                        fontWeight={700} 
                        gutterBottom 
                        sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                      >
                        No Transactions Yet
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 400, 
                          mx: 'auto',
                          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' 
                        }}
                      >
                        {transactionFilter === 'all'
                          ? 'Start bidding on auctions to begin earning recovery tokens!'
                          : `No ${transactionFilter} transactions found. Try a different filter.`}
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {transactions.map((tx) => {
                        const severity = getExpirationColor(tx.daysRemaining ?? 999);
                        const statusLabel =
                          tx.status === 'withdrawn'
                            ? 'Withdrawn'
                            : tx.status === 'expired'
                            ? 'Expired'
                            : 'Active';

                        return (
                          <Paper
                            elevation={0}
                            key={tx._id || `${tx.createdAt}-${tx.amount}`}
                            sx={{
                              p: 2.5,
                              borderRadius: compactRadius,
                              border: '1px solid',
                              borderColor:
                                statusLabel === 'Active'
                                  ? 'success.main'
                                  : statusLabel === 'Expired'
                                  ? 'error.main'
                                  : 'secondary.main',
                              bgcolor:
                                statusLabel === 'Active'
                                  ? theme.palette.mode === 'dark' ? 'rgba(76,175,80,0.08)' : 'rgba(76,175,80,0.05)'
                                  : statusLabel === 'Expired'
                                  ? theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.08)' : 'rgba(244,67,54,0.05)'
                                  : theme.palette.mode === 'dark' ? 'rgba(156,39,176,0.08)' : 'rgba(156,39,176,0.05)',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                boxShadow: theme.shadows[2],
                              },
                            }}
                          >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                              <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                                <Avatar
                                  variant="rounded"
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: compactRadius,
                                    bgcolor:
                                      statusLabel === 'Active'
                                        ? 'success.main'
                                        : statusLabel === 'Expired'
                                        ? 'error.main'
                                        : 'secondary.main',
                                  }}
                                >
                                  {statusLabel === 'Active' ? <NorthEastOutlinedIcon /> : statusLabel === 'Expired' ? '⏰' : <SouthEastOutlinedIcon />}
                                </Avatar>
                                <Stack spacing={0.5} flex={1}>
                                  <Typography 
                                    variant="subtitle1" 
                                    fontWeight={700}
                                    sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}
                                  >
                                    {tx.source || 'Auction Recovery'}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                                  >
                                    {formatDate(tx.createdAt)}
                                  </Typography>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      py: 0.5,
                                      px: 1.5,
                                      width: 'fit-content',
                                      borderRadius: '12px',
                                      bgcolor:
                                        statusLabel === 'Active'
                                          ? 'success.main'
                                          : statusLabel === 'Expired'
                                          ? 'error.main'
                                          : 'secondary.main',
                                      color:
                                        statusLabel === 'Active'
                                          ? 'success.contrastText'
                                          : statusLabel === 'Expired'
                                          ? 'error.contrastText'
                                          : 'secondary.contrastText',
                                    }}
                                  >
                                    <Typography variant="caption" fontWeight={700}>
                                      {statusLabel === 'Active'
                                        ? `Expires in ${formatTimeRemaining(tx.daysRemaining)}`
                                        : statusLabel}
                                    </Typography>
                                  </Paper>
                                </Stack>
                              </Stack>
                              <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.5}>
                                <Typography
                                  variant="h5"
                                  fontWeight={800}
                                  color={
                                    severity === 'error'
                                      ? 'error.main'
                                      : severity === 'warning'
                                      ? 'warning.main'
                                      : 'success.main'
                                  }
                                >
                                  +{formatTokens(tx.amount)}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    opacity: 0.7,
                                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' 
                                  }}
                                >
                                  ID: {tx._id ? tx._id.slice(-8) : '--'}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Paper>
                );
              })}

                      {totalPages > 1 && (
                        <Stack direction="row" justifyContent="center" sx={{ mt: 4 }}>
                          <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, value) => fetchTransactions(value)}
                            color="primary"
                            size={isMobile ? "medium" : "large"}
                            shape="rounded"
                            sx={{
                              '& .MuiPaginationItem-root': {
                                borderRadius: compactRadius,
                              },
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Stack>
          )}

      {/* Withdrawal Dialog */}
      <Dialog 
          open={showWithdrawModal} 
          onClose={() => setShowWithdrawModal(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: { borderRadius }
          }}
        >
          <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
            Confirm Withdrawal
            <IconButton
              onClick={() => setShowWithdrawModal(false)}
              sx={{ position: 'absolute', right: 12, top: 12 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 3 }}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: compactRadius,
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  bgcolor: theme.palette.action.hover,
                }}
              >
                <Typography 
                  variant="caption" 
                  textTransform="uppercase" 
                  fontWeight={600} 
                  sx={{ 
                    letterSpacing: '0.5px',
                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' 
                  }}
                >
                  Current Balance
                </Typography>
                <Typography 
                  variant="h4" 
                  fontWeight={800} 
                  sx={{ 
                    mt: 0.5,
                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' 
                  }}
                >
                  {formatTokens(walletData?.balance)}
                </Typography>
              </Paper>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      textAlign: 'center',
                      borderRadius: compactRadius,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(76,175,80,0.12)' : 'rgba(76,175,80,0.08)',
                      border: '1px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography variant="caption" color="success.main" fontWeight={600}>
                      You Receive (50%)
                    </Typography>
                    <Typography variant="h5" color="success.main" fontWeight={800} sx={{ mt: 1 }}>
                      {formatTokens(Math.floor((walletData?.balance || 0) * 0.5))}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      textAlign: 'center',
                      borderRadius: compactRadius,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.12)' : 'rgba(244,67,54,0.08)',
                      border: '1px solid',
                      borderColor: 'error.main',
                    }}
                  >
                    <Typography variant="caption" color="error.main" fontWeight={600}>
                      Amount Lost (50%)
                    </Typography>
                    <Typography variant="h5" color="error.main" fontWeight={800} sx={{ mt: 1 }}>
                      {formatTokens((walletData?.balance || 0) - Math.floor((walletData?.balance || 0) * 0.5))}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              <Alert 
                severity="warning" 
                icon={<WarningAmberOutlinedIcon />}
                sx={{ borderRadius: compactRadius }}
              >
                <Typography variant="body2" fontWeight={600}>
                  This action cannot be undone. 50% of your recovery balance will be permanently lost.
                </Typography>
              </Alert>
            </Stack>
        </DialogContent>
          <DialogActions sx={{ px: 3, py: 2.5 }}>
            <Button 
              onClick={() => setShowWithdrawModal(false)} 
              disabled={withdrawing}
              color="inherit"
              sx={{ borderRadius: compactRadius }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleWithdraw} 
              variant="contained" 
              color="primary" 
              disabled={withdrawing}
              disableElevation
              sx={{ borderRadius: compactRadius, px: 3, fontWeight: 600 }}
            >
              {withdrawing ? 'Processing…' : 'Confirm Withdrawal'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={closeSnackbar}
            action={
              <IconButton color="inherit" size="small" onClick={closeSnackbar}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ borderRadius: compactRadius }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default RecoveryWallet;
